import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

// SOP citation regex — matches "SOP A3", "SOP B12", "SOP K2", etc.
// Case-sensitive on the letter so we don't mistakenly link plain words.
const SOP_CITATION_RE = /\bSOP\s+([A-K])(\d+)\b/g;

// Simple module-level cache so we don't refetch the map for every block.
let _citationMap = null;
let _pendingFetch = null;

function useCitationMap() {
  const [map, setMap] = useState(_citationMap || {});
  useEffect(() => {
    if (_citationMap) { setMap(_citationMap); return; }
    if (!_pendingFetch) {
      _pendingFetch = api.get('/sop_library/citation_map')
        .then(r => { _citationMap = r.data || {}; return _citationMap; })
        .catch(() => { _citationMap = {}; return {}; });
    }
    _pendingFetch.then(m => setMap(m));
  }, []);
  return map;
}

// Render a text fragment turning "SOP X#" tokens into <Link>s.
function LinkifySopCitations({ text, map }) {
  if (!text) return null;
  if (!map || Object.keys(map).length === 0) return <>{text}</>;
  const parts = [];
  let lastIdx = 0;
  const re = new RegExp(SOP_CITATION_RE.source, 'g');
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    const code = `SOP ${m[1]}${m[2]}`;
    const pageId = map[code];
    if (pageId) {
      parts.push(
        <Link key={`${m.index}-${code}`} to={`/knowledge/${pageId}`}
              className="text-[hsl(var(--primary))] underline decoration-dotted hover:decoration-solid"
              data-sop-cite={code}>
          {code}
        </Link>
      );
    } else {
      parts.push(code);
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return <>{parts}</>;
}

export const BlockRenderer = ({ blocks }) => {
  const map = useCitationMap();
  if (!blocks?.length) {
    return <p className="text-[hsl(var(--foreground-subtle))] italic">This page is empty.</p>;
  }
  return (
    <div>
      {blocks.map((b, i) => <Block key={b.id || i} block={b} map={map} />)}
    </div>
  );
};

const T = ({ text, map }) => <LinkifySopCitations text={text} map={map} />;

const Block = ({ block, map }) => {
  const t = block.type;
  const text = block.text || '';
  if (t === 'heading1') return <h1><T text={text} map={map} /></h1>;
  if (t === 'heading2') return <h2><T text={text} map={map} /></h2>;
  if (t === 'heading3') return <h3><T text={text} map={map} /></h3>;
  if (t === 'paragraph') return <p className="whitespace-pre-wrap"><T text={text} map={map} /></p>;
  if (t === 'divider') return <div className="kn-divider" />;
  if (t === 'callout') return <div className="kn-callout"><T text={text} map={map} /></div>;
  if (t === 'code') return <p><span className="kn-code">{text}</span></p>;
  if (t === 'link') return <p><a className="text-[hsl(var(--primary))] underline" href={text} target="_blank" rel="noreferrer">{text}</a></p>;
  if (t === 'bulleted') {
    const items = block.items || (text ? [text] : []);
    return <ul>{items.map((it, i) => (
      <li key={i}><T text={typeof it === 'string' ? it : it?.text} map={map} /></li>
    ))}</ul>;
  }
  if (t === 'numbered') {
    const items = block.items || (text ? [text] : []);
    return <ol>{items.map((it, i) => (
      <li key={i}><T text={typeof it === 'string' ? it : it?.text} map={map} /></li>
    ))}</ol>;
  }
  if (t === 'checklist') {
    const items = block.items || [];
    return (
      <ul className="list-none pl-0 my-2">
        {items.map((it, i) => (
          <li key={i} className="kn-checklist-item">
            <input type="checkbox" defaultChecked={!!it.checked} />
            <span className={it.checked ? 'line-through text-[hsl(var(--foreground-muted))]' : ''}>
              <T text={it.text} map={map} />
            </span>
          </li>
        ))}
      </ul>
    );
  }
  if (t === 'table') {
    const rows = block.items || [];
    if (!rows.length) return null;
    const header = (block.meta?.header ? rows[0]?.row : null);
    const body = header ? rows.slice(1) : rows;
    return (
      <table>
        {header && <thead><tr>{header.map((h, i) => <th key={i}><T text={h} map={map} /></th>)}</tr></thead>}
        <tbody>
          {body.map((r, i) => (
            <tr key={i}>{(r.row || []).map((c, j) => <td key={j}><T text={c} map={map} /></td>)}</tr>
          ))}
        </tbody>
      </table>
    );
  }
  return <p><T text={text} map={map} /></p>;
};
